/**
 * TOC Generator for Flow2
 *
 * Automatically generates a Table of Contents screen from presentation content.
 * The TOC is inserted as screen #2 (index 1) after the title slide.
 *
 * @packageDocumentation
 * @module ai
 */

import {
  ScreenContent,
  ContentBlock,
  TOCEntry,
  TOCListBlock,
  TitleBlock,
  ImagePlaceholderBlock,
  generateBlockId,
  generateScreenId,
} from './schemas/ContentBlock';

/**
 * Minimum number of content screens (excluding title) to trigger TOC generation.
 * If flow has fewer than this many content screens, TOC is skipped.
 */
const MIN_CONTENT_SCREENS_FOR_TOC = 4;

/**
 * Screen types that are considered "section headers" (transitions, not content).
 * These will be displayed as group labels in the TOC, not numbered entries.
 */
const SECTION_HEADER_TYPES = new Set([
  'section-header',
  'title-slide',
]);

/**
 * Extract the title text from a screen's content blocks.
 * Looks for the first title or subtitle block.
 */
export function extractScreenTitle(screen: ScreenContent): string {
  for (const block of screen.blocks) {
    if (block.type === 'title') {
      return (block as TitleBlock).text;
    }
  }
  for (const block of screen.blocks) {
    if (block.type === 'subtitle') {
      return (block as any).text;
    }
  }
  // Fallback: use screen type formatted
  return screen.screenType
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Extract a representative image URL from a screen's content blocks.
 * Returns the first resolved image URL found, or undefined.
 */
function extractScreenImage(screen: ScreenContent): string | undefined {
  for (const block of screen.blocks) {
    if (block.type === 'image-placeholder') {
      const imgBlock = block as ImagePlaceholderBlock;
      if (imgBlock.resolvedImageUrl) {
        return imgBlock.resolvedImageUrl;
      }
    }
  }
  return undefined;
}

/**
 * Determine if a screen is a section header (transition) vs content.
 * Section headers are screens that only contain a title, or are explicitly marked as section-header type.
 */
export function isSectionHeader(screen: ScreenContent): boolean {
  // Explicit section header type
  if (SECTION_HEADER_TYPES.has(screen.screenType)) {
    return true;
  }

  // Check if screen only has title/subtitle (no real content)
  const contentBlocks = screen.blocks.filter(
    (b) => !['title', 'subtitle', 'speaker-notes'].includes(b.type)
  );

  // If no content blocks besides title/subtitle, it's a section header
  return contentBlocks.length === 0;
}

/**
 * Get the icon for a TOC entry.
 * - Section headers: empty string (displayed differently)
 * - Content with thumbnail: empty string (thumbnail shown instead)
 * - Content without thumbnail: the content number as string
 */
export function getTOCIcon(contentNumber: number | null, hasImage: boolean): string {
  if (contentNumber === null) {
    return ''; // Section header
  }
  if (hasImage) {
    return ''; // Will show thumbnail
  }
  return String(contentNumber);
}

/**
 * Generate a TOC screen from the presentation's content screens.
 *
 * Creates a hierarchical TOC where:
 * - Section headers become group labels (not numbered)
 * - Content screens are numbered sequentially
 * - Thumbnails are shown when available
 *
 * @param screens - All screens in the presentation (index 0 = title slide)
 * @returns A TOC ScreenContent to insert at index 1, or null if not enough screens
 */
export function generateTOCScreen(screens: ScreenContent[]): ScreenContent | null {
  // screens[0] is the title slide, screens[1..N] are content screens
  const contentScreens = screens.slice(1);

  if (contentScreens.length < MIN_CONTENT_SCREENS_FOR_TOC) {
    return null;
  }

  let contentNumber = 0;
  const entries: TOCEntry[] = contentScreens.map((screen, i) => {
    const isSection = isSectionHeader(screen);
    const imageUrl = extractScreenImage(screen);

    // Only increment content number for non-section screens
    if (!isSection) {
      contentNumber++;
    }

    return {
      screenIndex: i + 2, // +2 because title=0, toc=1
      title: extractScreenTitle(screen),
      icon: getTOCIcon(isSection ? null : contentNumber, !!imageUrl),
      imageUrl,
      isSection,
      contentNumber: isSection ? undefined : contentNumber,
    };
  });

  const tocListBlock: TOCListBlock = {
    id: generateBlockId(),
    type: 'toc-list',
    entries,
  };

  const titleBlock: ContentBlock = {
    id: generateBlockId(),
    type: 'title',
    text: 'Table of Contents',
    level: 1,
    role: 'heading',
  } as TitleBlock;

  return {
    id: 'screen-toc',
    screenType: 'toc',
    blocks: [titleBlock, tocListBlock],
  };
}

/**
 * Insert a TOC screen into a presentation's screens array.
 *
 * NOTE: TOC screens are no longer generated. The TOC is now accessible
 * via a modal triggered by a button in the player. This avoids a "meta"
 * screen breaking the content flow. The modal generates TOC dynamically
 * from screen titles.
 *
 * @param screens - The screens array (returned unchanged)
 * @returns The unmodified screens array
 */
export function insertTOCScreen(screens: ScreenContent[]): ScreenContent[] {
  // TOC is now a modal, not a dedicated screen
  return screens;
}
